import { fetchRepairItemHistoryBySerialNo } from '#/api/aftersalesMgmt/repairItem/fetchHistoryBySerialNo';
import { fetchRepairItemList } from '#/api/aftersalesMgmt/repairItem/fetchList';

/** 從報修明細列取得 repair_item id（establish 後 BE 宜回；缺則靠 SN 歷史／search 補）。 */
export function getRepairLineItemId(line) {
  if (!line || typeof line !== 'object') {
    return null;
  }
  return line.repairItemId ?? line.repairItem?.id ?? null;
}

/**
 * 報修單 IN_PROGRESS 且列有 repairItemId **或** serialNo 即可嘗試建立報告。
 * DRAFT 尚無維修品；COMPLETED 不再提供建立。
 * （BE example 未保證 line.repair_item_id；點擊時再 resolve。）
 */
export function canCreateRepairReportFromLine(requestStatus, line) {
  if (requestStatus !== 'IN_PROGRESS') {
    return false;
  }
  if (getRepairLineItemId(line)) {
    return true;
  }
  return Boolean(String(line?.serialNo ?? '').trim());
}

function matchHistoryEntry(entry, { repairRequestNo, serialNo }) {
  const item = entry?.repairItem ?? entry?.repair_item ?? null;
  if (!item?.id) {
    return false;
  }
  const itemSn = String(item.serialNo ?? '').trim();
  const wantSn = String(serialNo ?? '').trim();
  if (wantSn && itemSn && itemSn !== wantSn) {
    return false;
  }
  const itemReqNo = String(item.repairRequestNo ?? '').trim();
  const wantReqNo = String(repairRequestNo ?? '').trim();
  if (wantReqNo && itemReqNo && itemReqNo !== wantReqNo) {
    return false;
  }
  return true;
}

/**
 * 解析建立報告所需的 repairItemId。
 * 優先 line.repairItemId；否則 SN 歷史（對齊 repairRequestNo）；再 fallback search。
 *
 * @returns {Promise<string|null>}
 */
export async function resolveRepairItemIdForReportLine(line, {
  repairRequestNo = null,
} = {}) {
  const fromLine = getRepairLineItemId(line);
  if (fromLine) {
    return fromLine;
  }

  const serialNo = String(line?.serialNo ?? '').trim();
  if (!serialNo) {
    return null;
  }

  try {
    const history = await fetchRepairItemHistoryBySerialNo({
      serialNo,
      page: 1,
      pageSize: 50,
    });
    const rows = history?.data ?? [];
    const matched = rows.find((entry) => matchHistoryEntry(entry, {
      repairRequestNo,
      serialNo,
    }));
    const id = matched?.repairItem?.id
      ?? matched?.repair_item?.id
      ?? null;
    if (id) {
      return id;
    }
    // 無 requestNo 對齊時：取歷史第一筆（通常最新）且 SN 相符
    if (!repairRequestNo && rows[0]?.repairItem?.id) {
      return rows[0].repairItem.id;
    }
  } catch {
    // fall through to search
  }

  try {
    const list = await fetchRepairItemList({
      page: 1,
      pageSize: 50,
      keyword: serialNo,
      sort: [],
      filters: {},
    });
    const rows = list?.data ?? [];
    const matched = rows.find((item) => {
      if (String(item.serialNo ?? '').trim() !== serialNo) {
        return false;
      }
      if (!repairRequestNo) {
        return true;
      }
      return String(item.repairRequestNo ?? '').trim() === String(repairRequestNo).trim();
    });
    return matched?.id ?? null;
  } catch {
    return null;
  }
}
